import { lazy } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { IntlProvider } from 'dibs-react-intl';
import { Locale } from 'dibs-intl/exports/locales';
import { clientIntlPolyfill } from 'dibs-buyer-layout/exports/clientIntlPolyfill';
import { CheckoutServerVarsContextProvider } from '../components/shared/CheckoutServerVarsContext';
import { wait } from '../helpers/wait';
import { PRERENDER_CHECKOUT_ROOT_ID } from './constants';
import {
    addStoreUpdateListener,
    getPrerenderedCheckoutState,
    setPrerenderedCheckoutRootState,
    PRE_RENDER_STATE_VISIBLE,
    PRE_RENDER_STATE_PENDING,
    PRE_RENDER_STATE_ENABLED_IN_BACKGROUND,
    PRE_RENDER_STATE_NOT_INITIALIZED,
} from './prerenderedCheckoutStore';
import { handleBrowserButtons } from './redirectToPrerenderedCheckout';
import { getAbTestV2 } from 'dibs-buyer-layout/exports/clientABTestV2';
import { createCheckoutEnvironment } from '../environment';
import SV from 'server-vars';

const CheckoutRoot = lazy(() => import(/* webpackChunkName: "CheckoutRoot" */ '../CheckoutRoot'));

const DELAY_MS = 2000;

export type InitializePrerenderedCheckoutParams = {
    locale: Locale;
    messages: Record<string, string> | undefined;
    isMobile: boolean;
    itemId: string;
};

let lastParams: InitializePrerenderedCheckoutParams | null = null;
let reactRoot: Root | null = null;

export const initializePrerenderedCheckout = async (
    params: InitializePrerenderedCheckoutParams
): Promise<void> => {
    lastParams = params;
    const { locale, messages, isMobile, itemId } = params;

    if (!SV.get('enablers.preRenderCheckout')) {
        setPrerenderedCheckoutRootState(PRE_RENDER_STATE_NOT_INITIALIZED);
        return;
    }

    if (document.getElementById(PRERENDER_CHECKOUT_ROOT_ID)) {
        setPrerenderedCheckoutRootState(PRE_RENDER_STATE_NOT_INITIALIZED);
        throw Error(
            `<div id="${PRERENDER_CHECKOUT_ROOT_ID}" /> has already been created. "initializePrerenderedCheckout" function can not be called twice`
        );
    }

    const checkoutRootElement = document.createElement('div');
    checkoutRootElement.setAttribute('id', PRERENDER_CHECKOUT_ROOT_ID);
    document.getElementById('mainContent')?.appendChild(checkoutRootElement);

    handleBrowserButtons();

    /**
     * Timeout to give time PDP time to load, we don't want to slow things down.
     */
    const { promise, cancel } = wait(DELAY_MS);

    const removeStoreUpdateListener = addStoreUpdateListener(state => {
        if (state.rootState !== PRE_RENDER_STATE_PENDING) {
            cancel();
        }
    });
    await promise;
    removeStoreUpdateListener();
    if (getPrerenderedCheckoutState().rootState === PRE_RENDER_STATE_PENDING) {
        setPrerenderedCheckoutRootState(PRE_RENDER_STATE_ENABLED_IN_BACKGROUND);
    }

    await clientIntlPolyfill();

    const { createCheckoutStore } = await import(
        /* webpackChunkName: "CheckoutRootReducer" */ '../reducers/root'
    );

    reactRoot = createRoot(checkoutRootElement, { identifierPrefix: 'abc' });
    reactRoot.render(
        <IntlProvider locale={locale} messages={messages}>
            <CheckoutServerVarsContextProvider
                isMobile={isMobile}
                isEnhancedBuyerIncentivesClientVariant={
                    getAbTestV2('enhancedBuyerIncentives')?.variant === 'variant'
                }
            >
                <Provider store={createCheckoutStore()}>
                    <CheckoutRoot
                        itemId={itemId}
                        environment={createCheckoutEnvironment({
                            getGraphQLNetworkContext: () => {
                                if (
                                    getPrerenderedCheckoutState().rootState ===
                                    PRE_RENDER_STATE_VISIBLE
                                ) {
                                    return 'checkout';
                                }
                                return 'prerender-checkout';
                            },
                        })}
                    />
                </Provider>
            </CheckoutServerVarsContextProvider>
        </IntlProvider>
    );
};

export function refreshCheckout(): void {
    const checkoutRootElement = document.getElementById(PRERENDER_CHECKOUT_ROOT_ID);
    if (!lastParams || !checkoutRootElement) {
        /**
         * Do nothing because checkout was never pre-rendered.
         */
        return;
    }
    reactRoot?.unmount();
    document.getElementById(PRERENDER_CHECKOUT_ROOT_ID)?.remove();
    initializePrerenderedCheckout(lastParams);
}
